import { DeadlineType } from 'app/modules/deadlines/models';
import { QueueType } from 'app/modules/queues/models';

// query result types

export type AuditTrailAssetType =
  | 'team'
  | 'agent'
  | 'ai_agent'
  | 'entity'
  | 'case'
  | 'alert'
  | 'sar'
  | 'goaml'
  | 'fincen_ctr'
  | 'fincen_doep'
  | 'rule'
  | 'risk_model'
  | 'instrument'
  | 'fintrac_str'
  | 'ai_narrative_instructions'
  | 'rule_flow'
  | 'txn_event'
  | 'watchlist_entity';

// Audit log data types

export const VALID_AUDIT_LOG_ACTOR_TYPES = [
  'api',
  'system',
  'agent',
  'rule',
  'rule_flow',
  'sar',
] as const;

type AuditLogActorType = (typeof VALID_AUDIT_LOG_ACTOR_TYPES)[number];

export type AuditLogActor = {
  id: number | string;
  name: string;
  type: AuditLogActorType;
  isMissing?: boolean;
};

export const VALID_AUDIT_LOG_ASSET_TYPES = [
  'action_event',
  'agent',
  'alert',
  'attachment',
  'case',
  'deadline',
  'disposition',
  'entity',
  'entity_segment',
  'fincen_ctr',
  'fincen_ctr_batch',
  'fincen_doep',
  'fintrac_str',
  'goaml',
  'instrument',
  'investigation_checklist',
  'matchlist',
  'narrative',
  'narrative_template',
  'permission',
  'queue',
  'risk_model',
  'risk_score',
  'rule',
  'rule_flow',
  'sar',
  'sar_template',
  'subdisposition',
  'subdisposition_option',
  'tag',
  'team',
  'txn_event',
  'webhook',
  'watchlist_entity',
  'workflow_action',
  'ai_narrative_instructions',
  'ai_agent_task_definition',
  'custom_ai_agent_task',
  'ai_agent',
] as const;

type AuditLogAssetType = (typeof VALID_AUDIT_LOG_ASSET_TYPES)[number];

// Customer-Generated Data Object (CGDO) asset types. These are the only ones
// that carry an external_id, which is needed to resolve their pills in PG cache
// mode (detail endpoints are keyed on a hash of external_id, not the unit21 id).
export const CGDO_AUDIT_LOG_ASSET_TYPES = [
  'entity',
  'instrument',
  'txn_event',
  'action_event',
  'watchlist_entity',
] as const;

type CGDOAuditLogAssetType = (typeof CGDO_AUDIT_LOG_ASSET_TYPES)[number];

type BasicAuditLogAsset = {
  id: number | string;
  name: string;
  type: Exclude<AuditLogAssetType, CGDOAuditLogAssetType>;
  isMissing?: boolean;
};

type DeadlineAuditCustomData = {
  type: DeadlineType;
  date: string;
};

// AuditLogAsset when type === 'deadline'
export type DeadlineAuditLogAsset = {
  id: number;
  name: string;
  type: 'deadline';
  isMissing?: boolean;
  custom_data: DeadlineAuditCustomData;
};

// AuditLogAsset for CGDO types, which additionally carry an external_id.
export type CGDOAuditLogAsset = Omit<BasicAuditLogAsset, 'type'> & {
  type: CGDOAuditLogAssetType;
  externalId?: string;
};

export function isDeadlineAuditLogAsset(
  asset: AuditLogAsset,
): asset is DeadlineAuditLogAsset {
  return asset.type === 'deadline' && Object.hasOwn(asset, 'custom_data');
}

export function isCGDOAuditLogAsset(
  asset: AuditLogAsset,
): asset is CGDOAuditLogAsset {
  return CGDO_AUDIT_LOG_ASSET_TYPES.some((type) => type === asset.type);
}

export type AuditLogAsset =
  | BasicAuditLogAsset
  | DeadlineAuditLogAsset
  | CGDOAuditLogAsset;

export interface AuditServiceEntry {
  action: string;
  actor: AuditLogActor;
  asset: AuditLogAsset;
  details: AuditLogAsset[];
  submittedAt: string;
  createdAt: string;
  isImpersonating?: boolean;
}

// Chip prop types

export type BasicChipProps = {
  id: number | string;
  name: string;
  isMissing?: boolean;
  isAutomated?: boolean;
  aiResultType?: string;
};

// Chip props for CGDO types, which additionally carry an external_id used to
// resolve the pill in PG cache mode.
export type CGDOChipProps = {
  externalId?: string;
} & BasicChipProps;

type TypedChips<A, T extends string> = A & { type: T };

export type GenericChipProps = TypedChips<{ text: string }, 'generic'>;

export type AssetsChipProps =
  | TypedChips<
      BasicChipProps,
      Exclude<AuditLogAssetType, CGDOAuditLogAssetType>
    >
  | TypedChips<CGDOChipProps, CGDOAuditLogAssetType>;

export type ActorChipProps = TypedChips<BasicChipProps, AuditLogActorType>;

export type QueueChipProps = {
  queueType: QueueType;
} & BasicChipProps;

export type DateChipProps = {
  date: string;
};

export type DeadlineTypeChipProps = {
  deadlineType:
    | DeadlineType.ARTICLE_DEADLINE
    | DeadlineType.MANUAL_DEADLINE
    | DeadlineType.QUEUE_DEADLINE;
};

export type AssetChipComponentProps =
  | AssetsChipProps
  | ActorChipProps
  | TypedChips<QueueChipProps, 'queue_internal'>
  | TypedChips<DateChipProps, 'date'>
  | TypedChips<DeadlineTypeChipProps, 'deadline_internal'>
  | GenericChipProps;
